import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'

// The market size as typed. Thousands separators are dropped before it is read, as master's field
// did: read straight, the number stops at the first separator, so "1,000,000" came out as 1 and was
// floored to the minimum. The floor of 100 also catches anything that is not a number at all, which
// must not persist as NaN.
//
// A value reaching 10^21 is capped at the largest one below it, which still prints as plain digits.
// 10^21 is where master stopped: its field read the number back through a conversion that turns to
// exponent notation from there. Kept until a real upper limit is chosen; without it a long enough
// entry reads as Infinity and goes out as the market size.
const TAM_FLOOR = 100
export const TAM_MAX = 999999999999999900000

export const readTam = value => {
  const tam = parseInt(String(value ?? '').replace(/[^\d.-]/g, ''), 10)

  if (Number.isNaN(tam)) return TAM_FLOOR

  return Math.min(TAM_MAX, Math.max(TAM_FLOOR, tam))
}

export const setDefaults = ({ answer }) => {
  const periodArray = [
    { text: '1 HOUR', value: 1 },
    { text: '1 DAY', value: 2 },
    { text: '1 WEEK', value: 3 },
    { text: '1 MONTH', value: 4 },
    { text: '1 QUARTER', value: 5 },
    { text: '1 YEAR', value: 6 },
    { text: 'ONE-TIME', value: 0 }
  ]

  const availablePeriods = answer?.available_periods?.length ? answer.available_periods : [0]

  const product = parseInt(answer?.product?.[0] ?? 0, 10)

  const periodsList = periodArray.filter(p => availablePeriods.includes(p.value))
  const fqPeriodsList = periodArray.filter(p => availablePeriods.includes(p.value) && p.value !== 0)

  return {
    product,
    periodArray,
    availablePeriods,
    periodsList,
    fqPeriodsList
  }
}

// A figure too long for its column turns onto a second or third line at a thousands separator
// rather than pushing the table past the card. Only the break points are offered, after each
// digit-group comma, so the figure reads and copies as the service wrote it.
const breakAtDigitGroups = value => String(value).replace(/,(?=\d{3})/g, ',<wbr>')

export const calcTableProps = ({ answer, period, isLoading = false, clickable = false, filterRows = [], onClick }) => {
  if (!answer?.pointsOfInterest) {
    return { cols: [], rows: [], classNames: ['vankonan'] }
  }
  const pKey = `p${period}`
  const pointsOfInterestKey = answer?.pointsOfInterest?.[pKey] ? pKey : 'p0'

  const poi = answer?.pointsOfInterest?.[pointsOfInterestKey] || []

  if (!poi.length) {
    return { cols: [], rows: [], classNames: ['vankonan'] }
  }

  let tableHeader = [
    'PRICE.POINTS',
    'PRICE.SENSITIVITY.RESULTS',
    'UNIT.PRICE',
    'BUYERS',
    'REVENUE',
    'COST',
    'P&L'
  ].filter(value => poi[0][value] !== undefined)

  tableHeader = tableHeader.concat(
    Object.keys(poi[0]).filter(value => !tableHeader.includes(value) && value[0] !== '_')
  )

  const totalResp = formatAmounts(answer, 'total')

  const cols = tableHeader.map(item => {
    const isTitleCol = ['PRICE.POINTS', 'PRICE.SENSITIVITY.RESULTS'].includes(item)
    const baseTitle = item.split('.').join(' ')
    const extra = item === 'UNIT.PRICE' && answer?.oldVW?.[0] ? `<small>(from ${totalResp} resp.)</small>` : ''
    return {
      type: isTitleCol ? 'title' : 'value',
      title: `${baseTitle}${extra}`,
      tooltip: item === 'P&L' ? 'PROFIT/LOSS' : ''
    }
  })

  // Each row keeps its place in the full list: its figures and its colour are read by that place,
  // as master's table does, so a row hidden above it cannot shift what the rows below it show.
  const rows = poi
    .map((values, index) => ({ values, index }))
    .filter(({ values }) => filterRows.includes(values?._Show))
    .map(({ values, index }, i) => {
      const baseClassNames = values?._Bold === 'true' ? ['bold'] : []

      const cells = tableHeader.map(item => {
        const point = values?.[item]
        if (item === 'PRICE.POINTS') {
          const cell = {
            title: point,
            tooltip: values?._tooltip,
            colorIndex: (index % 21) + 1,
            classNames: baseClassNames
          }
          if (clickable) cell.onClick = onClick
          return cell
        }

        if (item === 'PRICE.SENSITIVITY.RESULTS') {
          const cell = {
            title: point,
            classNames: baseClassNames
          }
          if (clickable) cell.onClick = onClick
          return cell
        }

        if (item === 'UNIT.PRICE') {
          return {
            value: String(point).split('/person/person').join('/person'),
            format: breakAtDigitGroups,
            loading: isLoading,
            classNames: baseClassNames
          }
        }

        // Master's rule: every row says PROFIT or LOSS on hover, but only a highlighted (bold) row is
        // coloured, and a row with no profit figure takes no colour at all.
        if (item === 'P&L') {
          const profit = parseFloat(values?._Profit)
          const cls = profit > 0 ? 'positive-value' : profit < 0 ? 'negative-value' : profit === 0 ? 'neutral-value' : ''

          return {
            value: point,
            format: breakAtDigitGroups,
            tooltip: cls === 'positive-value' ? 'PROFIT' : cls === 'negative-value' ? 'LOSS' : '',
            loading: isLoading,
            classNames: cls && values?._Bold === 'true' ? [...baseClassNames, cls] : baseClassNames
          }
        }

        return {
          value: point,
          format: breakAtDigitGroups,
          loading: isLoading
        }
      })

      return { cells, position: i + 1 }
    })

  return { cols, rows, classNames: ['vankonan'] }
}

export const getPointName = ({ position, answer, period }) => {
  const pKey = `p${period}`

  const pointsOfInterestKey = answer.pointsOfInterest[pKey] ? pKey : 'p0'
  const points = answer.pointsOfInterest[pointsOfInterestKey]
  const item = points?.[position - 1]
  const ref = item?._Name || null

  return ref
}
